import { useMemo } from 'react';
import { ROADSIDE_ASSISTANCE } from '@rsos/capstone/src/constants/capabilities';
import useHasCapability from '@rsos/hooks/useHasCapability';
import {
  messageRequiresLocation,
  messageRequiresRoadside,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import {
  PreparedResponseType,
  SINGLETON_TYPE_ACTION_LABELS,
} from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesTypes';
import AssignedMessageRow, {
  type AssignedMessageSlot,
} from './AssignedMessageRow';
import styles from './AssignedMessages.module.css';

interface Props {
  messages: PreconfiguredResponse[];
}

const END_CHAT_SLOT: AssignedMessageSlot = {
  type: PreparedResponseType.ending,
  label: SINGLETON_TYPE_ACTION_LABELS[PreparedResponseType.ending],
};

const LOCATION_REQUEST_SLOT: AssignedMessageSlot = {
  type: PreparedResponseType.locationRequest,
  label: SINGLETON_TYPE_ACTION_LABELS[PreparedResponseType.locationRequest],
  messageFilter: messageRequiresLocation,
};

const ROADSIDE_SLOT: AssignedMessageSlot = {
  type: PreparedResponseType.roadsideAssistance,
  label: SINGLETON_TYPE_ACTION_LABELS[PreparedResponseType.roadsideAssistance],
  messageFilter: messageRequiresRoadside,
};

const AssignedMessages = ({ messages }: Props) => {
  // Location Request is not capability-gated: the LocationRequestLink smart
  // phrase carries no capability, so any authority can configure it (matching
  // End Chat). Roadside is gated because RoadsideAssistanceLink requires the
  // ROADSIDE_ASSISTANCE capability.
  const hasRoadsideAssistance =
    useHasCapability(ROADSIDE_ASSISTANCE).hasCapability;

  const slots = useMemo(() => {
    const visible: AssignedMessageSlot[] = [
      END_CHAT_SLOT,
      LOCATION_REQUEST_SLOT,
    ];
    if (hasRoadsideAssistance) visible.push(ROADSIDE_SLOT);
    return visible;
  }, [hasRoadsideAssistance]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Assigned Messages</h3>
        <p className={styles.description}>
          Assign a preconfigured message to send for each action. If no message
          is assigned, the default system message is used.
        </p>
      </div>
      <div className={styles.rows}>
        {slots.map(slot => (
          <AssignedMessageRow key={slot.type} slot={slot} messages={messages} />
        ))}
      </div>
    </div>
  );
};

export default AssignedMessages;
